import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Navbar from './Navbar';
import Footer from './Footer';
import AnimatedDemo from './AnimatedDemo';
import './LandingPageNew.css';

function LandingPageNew({ user, onLogout }) {
  const [activeFeature, setActiveFeature] = useState(0); // First item open by default
  const [loadingTier, setLoadingTier] = useState(null);
  const navigate = useNavigate();

  const handleSubscribe = async (tier) => {
    if (!user) {
      navigate('/signup');
      return;
    }

    setLoadingTier(tier);

    try {
      const response = await fetch('/api/create-checkout-session', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId: user.id,
          userEmail: user.email,
          tier: tier,
          interval: 'monthly',
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to create checkout session');
      }

      window.location.href = data.url;
    } catch (err) {
      console.error('Checkout error:', err);
      setLoadingTier(null);
    }
  };

  const features = [
    {
      title: 'Check your work',
      description: 'Upload documents, reports, websites, or paste your text in.'
    },
    {
      title: 'Compliance checks',
      description: 'Your inputs get checked against the latest AHPRA advertising regulations and guidelines.'
    },
    {
      title: 'Get instant feedback',
      description: 'See your compliance results instantly. Download as PDF or get them emailed to you.'
    },
    {
      title: 'Store your documents',
      description: 'Your compliant filing cabinet. Consent forms, licenses, CPD certificates — all stored securely in one place.'
    }
  ];

  const handleGetStarted = () => {
    if (user) {
      navigate('/');
    } else {
      navigate('/signup');
    }
  };

  return (
    <div className="landing-page-new">
      <Navbar user={user} onLogout={onLogout} />

      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-container">
          <h1 className="hero-logo">Mindmark</h1>
          <h2 className="hero-title">
            Check your work. Stay compliant.<br />
            Be confident.
          </h2>
          <p className="hero-subtitle">
            AI-powered compliance checking for Australian psychologists.<br />
            Scan websites, upload documents, or paste text — get instant<br />
            feedback against AHPRA regulations.
          </p>
          <div className="hero-buttons">
            <button onClick={handleGetStarted} className="btn-primary btn-large">
              Get Started
            </button>
            <Link to="/faq" className="btn-text">
              Learn more
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="20" height="20">
                <path d="M5 12h14m-7-7l7 7-7 7"/>
              </svg>
            </Link>
          </div>
        </div>
      </section>

      {/* Meet Mindmark Section */}
      <section className="meet-section">
        <div className="meet-container">
          <h2 className="meet-title">Meet Mindmark</h2>

          <div className="meet-content">
            {/* Animated Demo */}
            <div className="meet-demo">
              <AnimatedDemo />
            </div>

            {/* Feature Accordion - Claude Style */}
            <div className="meet-accordion">
              {features.map((feature, index) => (
                <React.Fragment key={index}>
                  <div
                    className={`meet-item ${activeFeature === index ? 'active' : ''}`}
                    onClick={() => setActiveFeature(index)}
                  >
                    <div className="meet-item-header">
                      <span className="meet-item-title">{feature.title}</span>
                    </div>
                    <div className={`meet-item-content ${activeFeature === index ? 'expanded' : ''}`}>
                      <p className="meet-item-description">{feature.description}</p>
                    </div>
                  </div>
                  {index < features.length - 1 && <div className="meet-item-separator" />}
                </React.Fragment>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="pricing-section">
        <div className="pricing-container">
          <h2 className="section-title">Get started with Mindmark</h2>

          <div className="pricing-cards three-tier">
            {/* Free Plan */}
            <div className="pricing-card">
              <h3 className="pricing-plan-name">Free</h3>
              <p className="pricing-plan-desc">Get started with compliance</p>

              <ul className="pricing-features">
                <li>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="18" height="18">
                    <polyline points="20 6 9 17 4 12"/>
                  </svg>
                  3 checks per month
                </li>
                <li>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="18" height="18">
                    <polyline points="20 6 9 17 4 12"/>
                  </svg>
                  Website & document analysis
                </li>
                <li>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="18" height="18">
                    <polyline points="20 6 9 17 4 12"/>
                  </svg>
                  Basic compliance reports
                </li>
                <li>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="18" height="18">
                    <polyline points="20 6 9 17 4 12"/>
                  </svg>
                  3 document storage
                </li>
              </ul>

              <div className="pricing-price">
                <span className="price-currency">A$</span>
                <span className="price-amount">0</span>
                <span className="price-period">/ month</span>
              </div>

              <button onClick={handleGetStarted} className="btn-secondary btn-full">
                Get Started
              </button>
            </div>

            {/* Basic Plan */}
            <div className="pricing-card featured">
              <h3 className="pricing-plan-name">Basic</h3>
              <p className="pricing-plan-desc">Everything in Free, plus:</p>

              <ul className="pricing-features">
                <li>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="18" height="18">
                    <polyline points="20 6 9 17 4 12"/>
                  </svg>
                  50 checks per month
                </li>
                <li>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="18" height="18">
                    <polyline points="20 6 9 17 4 12"/>
                  </svg>
                  Compliance Chat with AI
                </li>
                <li>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="18" height="18">
                    <polyline points="20 6 9 17 4 12"/>
                  </svg>
                  PDF report downloads
                </li>
                <li>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="18" height="18">
                    <polyline points="20 6 9 17 4 12"/>
                  </svg>
                  Full check history
                </li>
                <li>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="18" height="18">
                    <polyline points="20 6 9 17 4 12"/>
                  </svg>
                  50 document storage
                </li>
              </ul>

              <div className="pricing-price">
                <span className="price-currency">A$</span>
                <span className="price-amount">39</span>
                <span className="price-period">/ month</span>
              </div>

              <button onClick={() => handleSubscribe('basic')} disabled={loadingTier === 'basic'} className="btn-primary btn-full">
                {loadingTier === 'basic' ? 'Loading...' : 'Get Basic'}
              </button>
            </div>

            {/* Pro Plan */}
            <div className="pricing-card">
              <h3 className="pricing-plan-name">Pro</h3>
              <p className="pricing-plan-desc">Everything in Basic, plus:</p>

              <ul className="pricing-features">
                <li>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="18" height="18">
                    <polyline points="20 6 9 17 4 12"/>
                  </svg>
                  Unlimited compliance checks
                </li>
                <li>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="18" height="18">
                    <polyline points="20 6 9 17 4 12"/>
                  </svg>
                  Unlimited Compliance Chat
                </li>
                <li>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="18" height="18">
                    <polyline points="20 6 9 17 4 12"/>
                  </svg>
                  200 document storage
                </li>
                <li>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="18" height="18">
                    <polyline points="20 6 9 17 4 12"/>
                  </svg>
                  Priority support
                </li>
              </ul>

              <div className="pricing-price">
                <span className="price-currency">A$</span>
                <span className="price-amount">79</span>
                <span className="price-period">/ month</span>
              </div>

              <button onClick={() => handleSubscribe('pro')} disabled={loadingTier === 'pro'} className="btn-primary btn-full">
                {loadingTier === 'pro' ? 'Loading...' : 'Get Pro'}
              </button>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

export default LandingPageNew;
